import React, { useState, useEffect, useCallback } from 'react';
import { Container, Spinner, Card, Button } from 'react-bootstrap';
import axios from 'axios';
import Icon from '../components/Icon';
import './Overview.css';

function Overview() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/overview');
      setData(response.data);
      // Start with all groups collapsed
      setExpandedNodes(new Set());
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleNode = (nodeKey) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeKey)) {
        newSet.delete(nodeKey);
      } else {
        newSet.add(nodeKey);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    const allKeys = new Set();
    const collectKeys = (nodes, parentKey = '') => {
      nodes?.forEach((node, idx) => {
        const baseKey = parentKey || `root-${idx}`;
        const key = `${baseKey}/${node.type}:${node.code || node.name}`;
        allKeys.add(key);
        if (node.children?.length > 0) {
          collectKeys(node.children, key);
        }
      });
    };
    collectKeys(data?.hireTree);
    setExpandedNodes(allKeys);
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  const formatRate = (dayRate, weekRate) => {
    if (!dayRate && !weekRate) return null;
    const parts = [];
    if (dayRate) parts.push(`£${dayRate}/day`);
    if (weekRate) parts.push(`£${weekRate}/week`);
    return parts.join(', ');
  };

  const renderStockDetails = (node) => {
    const hasInclusions = node.inclusions && node.inclusions.length > 0;
    const hasReminders = node.reminders && node.reminders.length > 0;

    if (!hasInclusions && !hasReminders) {
      return <div className="stock-details text-muted small">No additional details</div>;
    }

    return (
      <div className="stock-details">
        {hasReminders && (
          <div className="detail-section">
            <div className="detail-row">
              <Icon name="bell" size={12} className="me-1 text-warning" />
              <span className="detail-label">Reminders ({node.reminders.length}):</span>
            </div>
            <ul className="detail-list">
              {node.reminders.map((rem, idx) => (
                <li key={idx}>
                  {rem.service_name}
                  {rem.due_date && (
                    <span className="text-muted">
                      {' '}
                      - Due: {new Date(rem.due_date).toLocaleDateString()}
                    </span>
                  )}
                  {rem.due_meter && <span className="text-muted"> - Meter: {rem.due_meter}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {hasInclusions && (
          <div className="detail-section">
            <div className="detail-row">
              <Icon name="link" size={12} className="me-1 text-info" />
              <span className="detail-label">Inclusions ({node.inclusions.length}):</span>
            </div>
            <ul className="detail-list inclusions-list">
              {node.inclusions.map((inc, idx) => (
                <li key={idx}>
                  <span className={`incl-type ${inc.inclType}`}>
                    [{inc.inclType === 'hire' ? 'H' : inc.inclType === 'sales' ? 'S' : '?'}]
                  </span>{' '}
                  <strong>{inc.incl_stockno || inc.incl_cat}</strong>
                  {inc.inclName && <span className="text-muted"> - {inc.inclName}</span>}
                  {inc.quantity > 1 && <span className="text-info"> ×{inc.quantity}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderTreeNode = (node, depth = 0, parentKey = '') => {
    // Create a unique key using type, code, and parent context
    const nodeKey = `${parentKey}/${node.type}:${node.code || node.name}`;
    const isExpanded = expandedNodes.has(nodeKey);
    const hasChildren = node.children && node.children.length > 0;
    const isStock = node.type === 'hire-stock' || node.type === 'hire-stock-bulk';

    // Determine letter and color based on type
    let letter, color;
    switch (node.type) {
      case 'hire-group':
        letter = 'G';
        color = '#6366f1';
        break;
      case 'hire-category':
        letter = 'C';
        color = '#8b5cf6';
        break;
      case 'hire-stock':
      case 'hire-stock-bulk':
        letter = 'S';
        color = '#06b6d4';
        break;
      default:
        letter = '?';
        color = '#6b7280';
    }

    return (
      <div key={nodeKey} className="tree-node">
        <div
          className={`tree-node-header ${hasChildren || isStock ? 'expandable' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren || isStock) toggleNode(nodeKey);
          }}
          style={{ paddingLeft: depth * 24 + 8 }}
        >
          {hasChildren || isStock ? (
            <span className="tree-toggle">{isExpanded ? '▼' : '▶'}</span>
          ) : (
            <span className="tree-toggle-placeholder"></span>
          )}
          <span className="tree-letter-icon" style={{ backgroundColor: color }}>
            {letter}
          </span>
          <span className="tree-code">{node.code}</span>
          <span className="tree-name">{node.name}</span>
          {hasChildren && <span className="tree-count">({node.children.length})</span>}
          {node.type === 'hire-category' && node.inclusions?.length > 0 && (
            <span className="tree-badge bg-info">{node.inclusions.length} cat incl</span>
          )}
          {isStock && (
            <>
              {node.inclusions?.length > 0 && (
                <span className="tree-badge bg-info">{node.inclusions.length} incl</span>
              )}
              {node.reminders?.length > 0 && (
                <span className="tree-badge bg-warning text-dark">{node.reminders.length} rem</span>
              )}
            </>
          )}
          {isStock && node.rate && <span className="tree-rate">{node.rate}</span>}
        </div>

        {isExpanded && (
          <div className="tree-children">
            {isStock ? (
              renderStockDetails(node)
            ) : (
              <>
                {/* Show category-level inclusions */}
                {node.type === 'hire-category' && node.inclusions?.length > 0 && (
                  <div className="category-inclusions">
                    <div className="detail-row">
                      <Icon name="link" size={12} className="me-1 text-info" />
                      <span className="detail-label">
                        Category Inclusions ({node.inclusions.length}):
                      </span>
                    </div>
                    <ul className="detail-list inclusions-list">
                      {node.inclusions.map((inc, idx) => (
                        <li key={idx}>
                          <span className={`incl-type ${inc.inclType}`}>
                            [{inc.inclType === 'hire' ? 'H' : inc.inclType === 'sales' ? 'S' : '?'}]
                          </span>{' '}
                          <strong>{inc.incl_stockno || inc.incl_cat}</strong>
                          {inc.inclName && <span className="text-muted"> - {inc.inclName}</span>}
                          {inc.quantity > 1 && <span className="text-info"> ×{inc.quantity}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {hasChildren &&
                  node.children.map((child, idx) => renderTreeNode(child, depth + 1, nodeKey))}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="py-4">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading data...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <div className="alert alert-danger">
          <strong>Error:</strong> {error}
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3 overview-page">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          <Icon name="sitemap" className="me-2" />
          Hire Overview
        </h4>
        <div>
          <Button variant="outline-secondary" size="sm" className="me-2" onClick={expandAll}>
            Expand All
          </Button>
          <Button variant="outline-secondary" size="sm" className="me-2" onClick={collapseAll}>
            Collapse All
          </Button>
          <Button variant="outline-primary" size="sm" onClick={fetchData}>
            <Icon name="refresh" className="me-1" /> Refresh
          </Button>
        </div>
      </div>

      <Card>
        <Card.Header className="py-2 d-flex align-items-center">
          <Icon name="truck" className="me-2" style={{ color: '#6366f1' }} />
          <strong>Hire Category Groups</strong>
          <span className="text-muted ms-2">
            ({data?.summary?.hireCategoryGroups || 0} groups, {data?.summary?.hireCategories || 0}{' '}
            categories,{' '}
            {(data?.summary?.hireStockIndividual || 0) + (data?.summary?.hireStockBulk || 0)} items)
          </span>
        </Card.Header>
        <Card.Body className="p-0 tree-container">
          {data?.hireTree?.length > 0 ? (
            data.hireTree.map((node, idx) => renderTreeNode(node, 0, `root-${idx}`))
          ) : (
            <p className="text-muted p-3 mb-0">No hire data found</p>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export default Overview;
